import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'reactstrap';
import { scopesApi } from '../../services/api';
import ScopeModal from './ScopeModal';

export default function ScopesPage() {
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [scopeModal, setScopeModal] = useState(false);
  const [editingScope, setEditingScope] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await scopesApi.getAll();
      setScopes(s);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditingScope(null);
    setScopeModal(true);
  }

  function openEdit(scope) {
    setEditingScope(scope);
    setScopeModal(true);
  }

  async function handleSave(dto) {
    if (editingScope) {
      await scopesApi.update(editingScope.id, dto);
    } else {
      await scopesApi.create(dto);
    }
    await load();
  }

  async function handleDelete(id) {
    try {
      await scopesApi.delete(id);
      setDeleteConfirm(null);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div>
      <div className="page-header d-flex align-items-center justify-content-between">
        <div>
          <h1 className="page-title">Scopes</h1>
          <p className="page-subtitle">Manage OAuth2 permission scopes</p>
        </div>
        <Button className="btn-primary" onClick={openCreate}>
          + New Scope
        </Button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="data-card">
        {loading ? (
          <div className="loading-state">Loading scopes...</div>
        ) : scopes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">&#128274;</div>
            <div>No scopes yet.</div>
            <Button className="btn-primary mt-3" onClick={openCreate}>
              Create your first scope
            </Button>
          </div>
        ) : (
          <table className="table admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scopes.map((s) => (
                <tr key={s.id}>
                  <td style={{ color: '#8892a4', fontSize: 12 }}>#{s.id}</td>
                  <td>
                    <span className="scope-tag" style={{ fontSize: 13 }}>{s.name}</span>
                  </td>
                  <td style={{ fontSize: 13, color: '#5b6b8a' }}>
                    {s.description || <span style={{ color: '#d0d5de' }}>—</span>}
                  </td>
                  <td>
                    <span className={s.isActive ? 'badge-active' : 'badge-inactive'}>
                      {s.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex" style={{ gap: 6 }}>
                      <button
                        className="btn-action btn-action-edit"
                        onClick={() => openEdit(s)}
                      >
                        Edit
                      </button>
                      {deleteConfirm === s.id ? (
                        <>
                          <button
                            className="btn-action btn-action-delete"
                            onClick={() => handleDelete(s.id)}
                          >
                            Confirm
                          </button>
                          <button
                            className="btn-action"
                            style={{ color: '#5b6b8a', borderColor: '#d0d5de' }}
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn-action btn-action-delete"
                          onClick={() => setDeleteConfirm(s.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ScopeModal
        isOpen={scopeModal}
        toggle={() => setScopeModal(false)}
        scope={editingScope}
        onSave={handleSave}
      />
    </div>
  );
}
